// src/components/WithFilter.tsx
import {
  useState,
  useCallback,
  FunctionComponent,
  ReactElement,
  ChangeEvent,
  KeyboardEvent,
} from "react";
import {
  Paper,
  Grid,
  InputBase,
  IconButton,
  makeStyles,
  Theme,
  createStyles,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      display: "flex",
      justifyContent: "space-between",
      height: 40,
    },
    input: {
      marginLeft: theme.spacing(1),
      flex: 1,
    },
    iconButton: {
      padding: 10,
    },
  })
);

interface WithFilterProps {
  onSearch: (searchableString: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const WithFilter: FunctionComponent<WithFilterProps> = ({
  onSearch,
  disabled = false,
  placeholder = "Search…",
}): ReactElement => {
  const classes = useStyles();
  const [value, setValue] = useState<string>("");

  const handleSearch = useCallback(() => {
    onSearch(value.trim());
  }, [onSearch, value]);

  const handleClear = useCallback(() => {
    setValue("");
    onSearch("");
  }, [onSearch]);

  const mediatorHandler = useCallback(
    (override?: string) => {
      onSearch(override ?? value.trim());
    },
    [onSearch, value]
  );

  const onEnterHandler = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.code === "Enter") mediatorHandler();
  };

  return (
    <Grid container justifyContent="flex-end" style={{ marginBottom: 16 }}>
      <Grid item xs={6} sm={4} md={3}>
        <Paper className={classes.paper}>
          <InputBase
            value={value}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setValue(e.target.value)
            }
            className={classes.input}
            disabled={disabled}
            onKeyUp={onEnterHandler}
            fullWidth
            placeholder={placeholder}
            inputProps={{ "aria-label": placeholder }}
          />
          <IconButton
            onClick={handleClear}
            disabled={disabled}
            className={classes.iconButton}
            aria-label="clear"
          >
            <ClearIcon />
          </IconButton>
          <IconButton
            onClick={handleSearch}
            disabled={disabled}
            className={classes.iconButton}
            aria-label="search"
          >
            <SearchIcon />
          </IconButton>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default WithFilter;
